/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useCallback, useMemo } from "react";

// internal-deps
import {
    RenderingDetails,
    ValueDrivenCrossSellXP,
    SavingsLineItemData,
    GiftingCartApiResponse,
    ApplyCouponSuggestion,
} from "@swiggy-private/cart-types";
import { VALID_RENDERING_DETAILS_TYPE } from "@swiggy-private/cart-ui";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCartRendererXP } from "./useCartRendererXP";
import { useDirectCouponSavingsData } from "./useDirectCouponSavingsData";
import { useAppliedCouponSavingsData } from "./useAppliedCouponSavingsData";
import { useCouponlessSavingsData } from "./useCouponlessSavingsData";
import { useCouponButtonSavingsData } from "./useCouponButtonSavingsData";
import { useIMCouponlessSavingsData } from "./useIMCouponlessSavingsData";

// helpers
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { getCartId, isPORV2Flow } from "@cart-client/helpers/Cart";
import {
    getApplyPaymentCouponWidgetData,
    getApplyCouponWidgetDataForRenderingDetail,
    isDirectCouponUsecase,
    isDirectPaymentCouponUsecase,
    buildGiftingExtraParamsForSavingsCornerAnalytics,
    buildGiftingCouponCodeList,
    buildMROCouponCodeAnalyticsContext,
} from "@cart-client/helpers/Coupon";
import { getSavingsCornerClickStreamData } from "@cart-client/helpers/SavingsCorner";

// analytics
import { impressionSavingsCornerLineItem } from "@cart-client/analytics/events";
import { CouponsAnalyticsExtraParams } from "@cart-client/interfaces/Coupon";

// constants
import { CouponType } from "@cart-client/constants/Coupon";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";

export interface IProps {
    couponSavingsRenderingDetails: RenderingDetails[];
}

export interface UseCouponSavingsReturnType {
    getCouponSavingsLineItems: () => SavingsLineItemData[];
}

export const useCouponSavings = ({ couponSavingsRenderingDetails }: IProps): UseCouponSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { data } = useContext(CartFlowContext);
    const { impressionEvent } = useContext(AnalyticsContext);

    const { xpSelectiveCouponVariant, valueDrivenCrossSellXPVariant, porFlowXP } = useCartRendererXP(cartResponse);
    // Restaurant coupon widget data is now resolved per rendering detail (see getCouponSavingsData).
    // Payment coupon widget data stays cart-level (combined cart).
    const applyPaymentCouponWidgetData = useMemo(() => getApplyPaymentCouponWidgetData(cartResponse), [cartResponse]);

    const isNewPORFlow = isPORV2Flow(porFlowXP);

    const cartId = !isEmpty(cartResponse) ? getCartId(cartResponse) : "";

    const getAnalyticsContextForCartTypes = useCallback(
        (restaurantId?: string | number) => {
            if (isGiftingCartSource(data?.cartSource)) {
                return [
                    buildGiftingExtraParamsForSavingsCornerAnalytics(cartResponse?.data as GiftingCartApiResponse),
                    buildGiftingCouponCodeList(cartResponse?.data as GiftingCartApiResponse),
                ];
            } else if (isMROFlow(data?.cartSource)) {
                return [
                    buildMROCouponCodeAnalyticsContext(cartResponse as unknown as MROReviewedCart, restaurantId),
                    {},
                ];
            }
            return [{}, {}];
        },
        [cartResponse, data?.cartSource],
    );

    const onSavingsLineItemLoad = useCallback(
        (title?: string, subtitle?: string, isPaymentCouponWidget?: boolean, restaurantId?: string | number): void => {
            const lineItemData = getSavingsCornerClickStreamData(title, subtitle, isPaymentCouponWidget);
            const [analyticsExtraParams, analyticsContext] = getAnalyticsContextForCartTypes(restaurantId);
            impressionSavingsCornerLineItem(
                impressionEvent,
                cartId,
                { ...lineItemData, ...analyticsContext },
                analyticsExtraParams as unknown as CouponsAnalyticsExtraParams,
            );
        },
        [getAnalyticsContextForCartTypes, impressionEvent, cartId],
    );

    const { getDirectCouponSavingsItem } = useDirectCouponSavingsData(
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
        isNewPORFlow,
    );
    const { getAppliedCouponSavingsItems } = useAppliedCouponSavingsData(
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
        isNewPORFlow,
    );
    const { getCouponLessSavingsItem } = useCouponlessSavingsData(onSavingsLineItemLoad);
    const { getCouponButtonSavingsItem } = useCouponButtonSavingsData(
        applyPaymentCouponWidgetData,
        onSavingsLineItemLoad,
    );

    const { getIMCouponLessSavingsItem } = useIMCouponlessSavingsData(onSavingsLineItemLoad);

    const getCouponSavingsData = useCallback(
        (renderingItemData: RenderingDetails) => {
            // Resolve coupon widget data for this specific rendering detail (MRO: per restaurant),
            // falling back to the global cart-level data for BAU/gifting.
            let applyCouponWidgetData = getApplyCouponWidgetDataForRenderingDetail(renderingItemData, cartResponse);

            // if the cart is an MRO cart and the restaurant id is present in the rendering detail,
            // then add the restaurant id to the apply coupon widget data for back sync to child cart
            if (isMROFlow(data?.cartSource) && !isEmpty(renderingItemData?.meta?.restaurantId)) {
                applyCouponWidgetData = {
                    ...((applyCouponWidgetData as ApplyCouponSuggestion) || {}),
                    mroChildCartRestaurantId: renderingItemData?.meta?.restaurantId || "",
                } as ApplyCouponSuggestion;
            }

            switch (renderingItemData.type.toUpperCase()) {
                case VALID_RENDERING_DETAILS_TYPE.COUPON_BUTTON: {
                    const isDirectCouponScenario = isDirectCouponUsecase(
                        applyCouponWidgetData,
                        xpSelectiveCouponVariant,
                        valueDrivenCrossSellXPVariant,
                    );

                    if (isDirectCouponScenario) {
                        return getDirectCouponSavingsItem(
                            valueDrivenCrossSellXPVariant === ValueDrivenCrossSellXP.COUPON_LOCKED_WIDGET,
                            renderingItemData,
                            applyCouponWidgetData,
                        );
                    }
                    return getCouponButtonSavingsItem(renderingItemData, applyCouponWidgetData);
                }

                case VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_BUTTON: {
                    if (!isNewPORFlow) {
                        return [];
                    }
                    const isDirectPaymentCouponScenario = isDirectPaymentCouponUsecase(applyPaymentCouponWidgetData);
                    if (isDirectPaymentCouponScenario) {
                        return getDirectCouponSavingsItem(
                            false,
                            renderingItemData,
                            applyPaymentCouponWidgetData,
                            CouponType.PAYMENT,
                        );
                    }
                    return getCouponButtonSavingsItem(renderingItemData, applyCouponWidgetData);
                }

                case VALID_RENDERING_DETAILS_TYPE.PAYMENT_COUPON_REMOVE_BUTTON:
                    if (!isNewPORFlow) {
                        return [];
                    }
                    return getAppliedCouponSavingsItems(
                        renderingItemData.icon,
                        applyPaymentCouponWidgetData,
                        CouponType.PAYMENT,
                    );

                case VALID_RENDERING_DETAILS_TYPE.COUPON_REMOVE_BUTTON:
                    return getAppliedCouponSavingsItems(renderingItemData.icon, applyCouponWidgetData);

                case VALID_RENDERING_DETAILS_TYPE.COUPONLESS_SAVINGS:
                    return getCouponLessSavingsItem(renderingItemData.icon, renderingItemData.meta);

                case VALID_RENDERING_DETAILS_TYPE.IM_COUPONLESS_SAVINGS:
                    return getIMCouponLessSavingsItem(renderingItemData.icon);
            }
        },
        [
            cartResponse,
            applyPaymentCouponWidgetData,
            getAppliedCouponSavingsItems,
            getCouponButtonSavingsItem,
            getCouponLessSavingsItem,
            getDirectCouponSavingsItem,
            isNewPORFlow,
            getIMCouponLessSavingsItem,
            valueDrivenCrossSellXPVariant,
            xpSelectiveCouponVariant,
            data?.cartSource,
        ],
    );

    const getCouponSavingsLineItems = useCallback(() => {
        return couponSavingsRenderingDetails.reduce<SavingsLineItemData[]>((acc, renderingDetail) => {
            const lineItemData = getCouponSavingsData(renderingDetail);

            if (!isEmpty(lineItemData)) {
                Array.isArray(lineItemData) ? acc.push(...lineItemData) : acc.push(lineItemData);
            }
            return acc;
        }, []);
    }, [couponSavingsRenderingDetails, getCouponSavingsData]);

    return { getCouponSavingsLineItems };
};
